import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { searchBookings } from '../lib/bookings';
import { formatError } from '../lib/errors';
import type { BookingRow, LifecycleStatus } from '../types';
import { BookingCard } from '../components/BookingCard';
import { searchVouchers, voucherStatusOf, type DiscountCodeRow } from '../lib/vouchers';

export function SearchScreen() {
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [results, setResults] = useState<BookingRow[]>([]);
  const [vouchers, setVouchers] = useState<DiscountCodeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setResults([]);
      setVouchers([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Bookings and vouchers in parallel; a voucher-search failure must not
      // hide the booking results, so it degrades to an empty section.
      const [list, codes] = await Promise.all([
        searchBookings(query),
        searchVouchers(query).catch(() => [] as DiscountCodeRow[]),
      ]);
      list.sort((a, b) => {
        const aS = a.booking_slots[0]?.slot_start ?? a.created_at;
        const bS = b.booking_slots[0]?.slot_start ?? b.created_at;
        return bS.localeCompare(aS);
      });
      setResults(list);
      setVouchers(codes);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (submitted) void run(submitted);
  }, [submitted, run]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(q);
  };

  const onLifecycleChange = (id: string, next: LifecycleStatus) => {
    setResults((prev) =>
      prev.map((b) => (b.id === id ? { ...b, lifecycle_status: next } : b)),
    );
  };

  return (
    <div className="screen search-screen">
      <form onSubmit={onSubmit} className="search-form">
        <input
          autoFocus
          type="search"
          placeholder="Boekingsnummer, vouchercode, e-mail, naam, telefoon…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit" className="btn-primary">Zoek</button>
      </form>

      {loading ? <div className="muted">Zoeken…</div> : null}
      {error ? <div className="error-card">{error}</div> : null}
      {!loading && submitted && results.length === 0 && vouchers.length === 0 && !error ? (
        <div className="muted">Geen boekingen of vouchers gevonden voor "{submitted}".</div>
      ) : null}

      {vouchers.length > 0 ? (
        <section className="search-vouchers">
          <h2>Vouchers</h2>
          <ul className="search-voucher-list">
            {vouchers.map((v) => {
              const status = voucherStatusOf(v);
              const discountStr =
                v.kind === 'percent'
                  ? `${v.percent_off}%`
                  : `€ ${((v.flat_off_cents ?? 0) / 100).toFixed(2)}`;
              return (
                <li key={v.id}>
                  <Link to={`/voucher/${v.code}`} className="voucher-code voucher-code-link">
                    {v.code}
                  </Link>
                  <span className={`voucher-pill ${status.className}`}>{status.label}</span>
                  <span className="muted">
                    {discountStr}
                    {v.max_party_size != null ? ` · max. ${v.max_party_size} tickets` : ''}
                    {v.buyer_name || v.buyer_email ? ` · ${v.buyer_name ?? v.buyer_email}` : ''}
                    {v.notes && !v.buyer_email ? ` · ${v.notes}` : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <div className="booking-list">
        {results.map((b) => (
          <BookingCard
            key={b.id}
            booking={b}
            onLifecycleChange={onLifecycleChange}
            showDate
          />
        ))}
      </div>
    </div>
  );
}
